import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Users, DollarSign, Edit, Copy, Search } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useBookings, useUpdateBookingStatus, Booking } from '@/hooks/useBookings';
import { truncateText } from '@/utils/stringUtils';
import { useToast } from '@/hooks/use-toast';
import { parseDateFromDatabase } from '@/utils/dateParser';
import BookingFormModal from './BookingFormModal';

const AdminBookingsTab = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { data: bookings = [], isLoading, error } = useBookings();
  const updateStatusMutation = useUpdateBookingStatus();
  const { toast } = useToast();

  const handleNewBooking = () => {
    setSelectedBooking(null);
    setShowModal(true);
  };

  const handleEditBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowModal(true);
  };

  const handleCancelBooking = (booking: Booking) => {
    if (window.confirm('¿Estás seguro de que quieres cancelar esta reservación?')) {
      updateStatusMutation.mutate({ id: booking.id, status: 'Cancelada' });
    }
  };

  const handleActivateBooking = (booking: Booking) => {
    updateStatusMutation.mutate({ id: booking.id, status: 'Reservada' });
  };

  const handleCopyUrl = async (bookingId: string) => {
    const url = `${window.location.origin}/${bookingId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "URL copiada",
        description: "El enlace de la reservación ha sido copiado al portapapeles",
      });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast({
        title: "Error",
        description: "No se pudo copiar el enlace",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Reservada':
        return <Badge className="bg-green-100 text-green-800">Reservada</Badge>;
      case 'Cancelada':
        return <Badge variant="destructive">Cancelada</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount);
  };

  const formatBookingDate = (dateString: string) => {
    const date = parseDateFromDatabase(dateString);
    if (!date) return dateString;
    
    return format(date, 'dd/MMM/yyyy', { locale: es });
  };

  // Filter bookings based on search term
  const filteredBookings = bookings.filter((booking) => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const guestNameMatch = booking.guest_name.toLowerCase().includes(searchLower);
    const propertyMatch = booking.property_short_name.toLowerCase().includes(searchLower);
    
    return guestNameMatch || propertyMatch;
  });

  // Sort filtered bookings by check-in date (most recent first)
  const sortedBookings = [...filteredBookings].sort((a, b) => {
    const dateA = new Date(a.check_in);
    const dateB = new Date(b.check_in);
    return dateB.getTime() - dateA.getTime();
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="text-gray-500">Cargando reservaciones...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-medium">Error al cargar reservaciones</h3>
        <p className="text-red-600 text-sm mt-1">
          No se pudieron cargar las reservaciones. Por favor, intenta nuevamente.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Reservaciones</h2>
        <Button onClick={handleNewBooking} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Reservación
        </Button>
      </div>

      {/* Search Filter - simplified style like properties tab */}
      <div className="flex-1 relative max-w-md">
        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Buscar por nombre de huésped o propiedad..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {bookings.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-gray-800 font-medium mb-2">No hay reservaciones</h3>
          <p className="text-gray-600 text-sm mb-4">
            No se encontraron reservaciones en la base de datos.
          </p>
          <Button onClick={handleNewBooking} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Crear Primera Reservación
          </Button>
        </div>
      ) : sortedBookings.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-gray-800 font-medium mb-2">No se encontraron resultados</h3>
          <p className="text-gray-600 text-sm mb-4">
            No hay reservaciones que coincidan con tu búsqueda "{searchTerm}".
          </p>
          <Button 
            variant="outline" 
            onClick={() => setSearchTerm('')}
          >
            Limpiar búsqueda
          </Button>
        </div>
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Huésped
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Llegada
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Salida
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Noches
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pago Recibido
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Propiedad
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Origen
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">
                      {booking.booking_id}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{booking.guest_name}</div>
                        {booking.guest_email && (
                          <div className="text-gray-500 text-xs">{booking.guest_email}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {formatBookingDate(booking.check_in)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {formatBookingDate(booking.check_out)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <span className="font-medium">{booking.nights}</span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <span className="font-medium">{formatCurrency(booking.payout ?? 0)}</span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <span className="font-medium" title={booking.property_short_name}>
                        {truncateText(booking.property_short_name, 20)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {booking.origins?.name || 'N/A'}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      {getStatusBadge(booking.status)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditBooking(booking)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopyUrl(booking.booking_id)}
                          title="Copiar enlace de huésped"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        {booking.status === 'Reservada' ? (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleCancelBooking(booking)}
                            disabled={updateStatusMutation.isPending}
                          >
                            Cancelar
                          </Button>
                        ) : (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleActivateBooking(booking)}
                            disabled={updateStatusMutation.isPending}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Activar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <BookingFormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        booking={selectedBooking}
      />
    </div>
  );
};

export default AdminBookingsTab;
